import { UserGroupIcon } from "@heroicons/react/24/outline";
import Header from "common/components/Header/Header";
import FilteredList from "common/components/Table/FilteredList";
import useRouter from "common/hooks/use-router";

import useItem from "common/hooks/useItem";
import useList from "common/hooks/useList";
import { useCallback, useContext, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";

import AlertsContext from "common/providers/alerts";
import useAuth from "common/hooks/useAuth";
import { UserType } from "types/UserType";
import UserTypeItem from "./UserTypeItem";

function UserTypes() {
  const { t } = useTranslation();
  const { navigate } = useRouter();
  const { setAlert } = useContext(AlertsContext);

  const { user: currentUser } = useAuth();
  const canManageUserTypes = currentUser?.isSuperadmin;

  const {
    fetchItems,
    items,
    isFetching,
    filters,
    setFilter,
    pageCount,
    setPageIndex,
    pageIndex,
    removeListItem,
    itemsCount,
    itemsPerPage,
    error,
  } = useList<UserType>("user-types", {
    defaultFilters: {},
    defaultSort: "name:ASC",
  });
  const { removeItem } = useItem<UserType>("user-types", "");

  useEffect(() => {
    if (error) {
      setAlert(error, "danger");
    }
  }, [error, setAlert]);

  const handleDelete = useCallback(
    async (id: any) => {
      try {
        removeListItem(id);
        await removeItem(id);
        fetchItems();
      } catch (e) {
        setAlert(e, "danger");
        return e;
      }
      return true;
    },
    [removeListItem, fetchItems, removeItem, setAlert]
  );

  const handleNavigate = (id: any) => {
    navigate(`/usertypes/edit/${id}`);
  };

  const columns = useMemo(
    () => [
      {
        Header: t("forms.name"),
        filterName: "name$contains",
        filterLabel: t("filters.search"),
        columnClasses: "w-1/2",
      },
    ],
    [t]
  );

  return (
    <div className="mx-auto max-w-full py-4 px-4 text-slate-500">
      <Header
        title={t("titlePages.userTypes")}
        subTitle={t("titleLegends.userTypes")}
        btnTitle={t("newItem.userType")}
        btnSubtitle={t("newItem.userTypeLegend")}
        Icon={UserGroupIcon}
        addLink="/usertypes/add"
        addLinkDisabled={!canManageUserTypes}
        isBeforeFilters
      />

      <FilteredList
        data={items ?? []}
        columns={columns} // used as filter items
        isFetching={isFetching}
        pageCount={pageCount}
        filters={filters}
        setFilter={setFilter}
        setPageIndex={setPageIndex}
        pageIndex={pageIndex}
        RenderItem={UserTypeItem}
        onDelete={handleDelete}
        onNavigate={handleNavigate}
        itemsCount={itemsCount}
        itemsPerPage={itemsPerPage}
      />
    </div>
  );
}

export default UserTypes;
